import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Phone, MapPin, Mail, Users, Maximize2, BedDouble, Star, Play, Bus, Car, Train, ArrowRight,
} from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import MobileBookCTA from "@/components/MobileBookCTA";
import WhatsAppButton from "@/components/WhatsAppButton";
import SEO from "@/components/SEO";
import {
  Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious,
} from "@/components/ui/carousel";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";

const REAL_PHOTOS = {
  hero: "https://imgkit.otelz.com/turkiye/karabuk/safranbolu/fatma-hanim-konai_e6e121b6.jpg?tr=w-2200,q-90",
  about_1: "https://imgkit.otelz.com/turkiye/karabuk/safranbolu/fatma-hanim-konai_1e6ff57c.jpg?tr=w-1400,q-90",
  about_2: "https://imgkit.otelz.com/turkiye/karabuk/safranbolu/fatma-hanim-konai_bba425e0.jpg?tr=w-900,q-90",
  rooms_pull: "https://imgkit.otelz.com/turkiye/karabuk/safranbolu/fatma-hanim-konai_bf041e19.jpg?tr=w-2200,q-90",
};

/* ============ HERO ============ */
const Hero = ({ content }) => {
  const { t, lang } = useLanguage();
  const heroImg = content?.hero_image || REAL_PHOTOS.hero;
  const title = content?.hero_title?.[lang] || t("hero.title");
  const subtitle = content?.hero_subtitle?.[lang] || t("hero.subtitle");

  return (
    <section data-testid="hero-section" className="relative h-[100svh] min-h-[640px] w-full overflow-hidden">
      <img src={heroImg} alt={title} className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 hero-overlay-mag" />

      <div className="hidden md:block absolute left-6 lg:left-10 top-1/2 -translate-y-1/2 text-[#FAF6EC]/85">
        <div className="vlabel">N° 78600 · Karabük · Türkiye</div>
      </div>
      <div className="hidden md:block absolute right-6 lg:right-10 top-1/2 -translate-y-1/2 text-[#FAF6EC]/85">
        <div className="vlabel">Est. 1905 · UNESCO 1994</div>
      </div>

      <div className="absolute inset-0 flex flex-col items-center justify-center text-[#FAF6EC] text-center px-6 fade-up">
        <div className="font-mono text-[10px] uppercase tracking-[0.42em] text-[#FAF6EC]/80 mb-6">
          Boutique Mansion · Safranbolu
        </div>
        <h1 className="font-display text-[clamp(3rem,8.5vw,7.5rem)] leading-[0.95] tracking-tight">
          {title}
        </h1>
        <p className="mt-7 max-w-xl text-[15px] text-[#FAF6EC]/85 font-light leading-[1.85]">{subtitle}</p>
        <div className="mt-10 flex flex-wrap gap-4 justify-center">
          <Link to="/booking" data-testid="hero-book-btn" className="btn-outline-cream">
            {t("hero.cta_book")} <ArrowRight size={13} />
          </Link>
        </div>
      </div>

      <div className="cue-line" />
    </section>
  );
};

/* ============ CHAPTER HEADER ============ */
const ChapterHeader = ({ num, eyebrow, title }) => (
  <div className="flex flex-col items-start gap-5 mb-12 lg:mb-16">
    <div className="flex items-center gap-4">
      <span className="chapter-num">No. {num}</span>
      <span className="rule-brass w-12" />
      <span className="eyebrow">{eyebrow}</span>
    </div>
    <h2 className="display-lg max-w-3xl">{title}</h2>
  </div>
);

/* ============ ABOUT ============ */
const About = ({ content }) => {
  const { t, lang } = useLanguage();
  const title = content?.about_title?.[lang] || t("about.title");
  const body1 = content?.about_body_1?.[lang] || t("about.body_1");
  const body2 = content?.about_body_2?.[lang] || t("about.body_2");
  const stats = [
    { v: content?.stats_years || "110+", k: "about.stat_years" },
    { v: content?.stats_rooms || "11", k: "about.stat_rooms" },
    { v: content?.stats_unesco || "1994", k: "about.stat_unesco" },
    { v: content?.stats_breakfast || "100+", k: "about.stat_breakfast" },
  ];
  return (
    <section id="about" data-testid="about-section" className="py-28 lg:py-44">
      <div className="container-mag">
        <ChapterHeader num="01" eyebrow={t("about.kicker")} title={title} />

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-7 zoom-on-hover">
            <img src={REAL_PHOTOS.about_1} alt="" className="w-full h-[420px] lg:h-[640px] object-cover" />
          </div>

          <div className="lg:col-span-5 flex flex-col">
            <p className="body-prose first-letter:font-display first-letter:text-7xl first-letter:font-normal first-letter:float-start first-letter:me-3 first-letter:leading-[0.85] first-letter:text-[#1E3B2E]">
              {body1}
            </p>
            <div className="my-8 rule-brass w-16" />
            <p className="body-prose">{body2}</p>

            <div className="grid grid-cols-2 gap-8 mt-12 pt-8 border-t border-[#CFC4A8]">
              {stats.map((s) => (
                <div key={s.k}>
                  <div className="font-display text-5xl text-[#0F0E0C] ltr-numbers">{s.v}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#5C5448] mt-2">{t(s.k)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Side image to break grid */}
        <div className="mt-12 lg:mt-20 grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-5 md:col-start-2 zoom-on-hover">
            <img src={REAL_PHOTOS.about_2} alt="" className="w-full h-[420px] object-cover" />
          </div>
          <div className="hidden md:flex md:col-span-4 md:col-start-8 items-end">
            <div>
              <p className="font-display italic text-3xl lg:text-4xl text-[#1E3B2E] leading-[1.2]">
                “{(lang === 'en' ? 'A bridge between memory and modernity.' : lang === 'ar' ? 'جسر بين الذاكرة والحداثة.' : 'Hafıza ile modernite arasında bir köprü.')}”
              </p>
              <div className="mt-5 font-mono text-[10px] uppercase tracking-[0.32em] text-[#5C5448]">— Hatıra defterinden</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============ ROOMS ============ */
const RoomsSection = ({ rooms }) => {
  const { t, lang } = useLanguage();
  if (!rooms.length) return null;
  return (
    <section id="rooms" data-testid="rooms-section" className="py-28 lg:py-40 bg-[#F2EDDF]">
      <div className="container-mag">
        <ChapterHeader num="02" eyebrow={t("rooms.kicker")} title={t("rooms.title")} />

        <div className="space-y-20 lg:space-y-28">
          {rooms.map((room, i) => {
            const reverse = i % 2 === 1;
            return (
              <article
                key={room.id}
                data-testid={`room-card-${room.slug}`}
                className="room-mag-card grid lg:grid-cols-12 gap-8 lg:gap-14 items-center"
              >
                <div className={`lg:col-span-7 zoom-on-hover ${reverse ? "lg:order-2" : ""}`}>
                  <img
                    src={room.images?.[0]}
                    alt={room.name[lang] || room.name.tr}
                    className="w-full h-[420px] lg:h-[560px] object-cover"
                  />
                </div>
                <div className={`lg:col-span-5 ${reverse ? "lg:order-1" : ""}`}>
                  <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#1E3B2E] mb-4">
                    Suite N° 0{i + 1}
                  </div>
                  <h3 className="display-md mb-5">{room.name[lang] || room.name.tr}</h3>
                  <p className="body-prose">
                    {room.description[lang] || room.description.tr}
                  </p>

                  <div className="grid grid-cols-3 gap-3 mt-8 pt-8 border-t border-[#CFC4A8] text-xs">
                    <div>
                      <Users size={15} className="text-[#1E3B2E]" />
                      <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#5C5448] mt-2">{t("rooms.capacity")}</div>
                      <div className="text-[#0F0E0C] mt-1 ltr-numbers">{room.capacity}</div>
                    </div>
                    <div>
                      <Maximize2 size={15} className="text-[#1E3B2E]" />
                      <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#5C5448] mt-2">{t("rooms.size")}</div>
                      <div className="text-[#0F0E0C] mt-1 ltr-numbers">{room.size_m2} m²</div>
                    </div>
                    <div>
                      <BedDouble size={15} className="text-[#1E3B2E]" />
                      <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#5C5448] mt-2">{t("rooms.bed")}</div>
                      <div className="text-[#0F0E0C] mt-1 text-[11px] leading-snug">
                        {(room.bed_type && (room.bed_type[lang] || room.bed_type.tr)) || "—"}
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 flex items-end justify-between">
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#5C5448]">{t("rooms.from")}</div>
                      <div className="font-display text-4xl text-[#0F0E0C] ltr-numbers mt-1">
                        ₺{room.price_try.toLocaleString("tr-TR")}
                        <span className="text-sm text-[#5C5448] ms-2">{t("rooms.per_night")}</span>
                      </div>
                    </div>
                    <Link
                      to={`/booking?room=${room.id}`}
                      data-testid={`room-book-${room.slug}`}
                      className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#1E3B2E] hover:text-[#0F0E0C] inline-flex items-center gap-2 border-b border-[#1E3B2E] pb-1"
                    >
                      {t("rooms.book_now")} <ArrowRight size={13} />
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ============ GALLERY ============ */
const Gallery = ({ images }) => {
  const { t } = useLanguage();
  if (!images.length) return null;
  return (
    <section id="gallery" data-testid="gallery-section" className="py-28 lg:py-40">
      <div className="container-mag">
        <ChapterHeader num="03" eyebrow={t("gallery.kicker")} title={t("gallery.title")} />
        <div className="masonry">
          {images.map((img, i) => (
            <Dialog key={img.id || i}>
              <DialogTrigger asChild>
                <button data-testid={`gallery-thumb-${i}`} className="block w-full zoom-on-hover">
                  <img src={img.url || img} alt="" className="w-full h-auto object-cover" />
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-5xl bg-transparent border-0 p-0">
                <img src={img.url || img} alt="" className="w-full h-auto" />
              </DialogContent>
            </Dialog>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ============ FULL-BLEED PULL QUOTE ============ */
const PullImage = () => {
  const { lang } = useLanguage();
  const quote = lang === "en"
    ? "Stone walls. Wooden ceilings. A city wrapped in saffron."
    : lang === "ar"
    ? "جدران من حجر. أسقف من خشب. مدينة ملفوفة بالزعفران."
    : "Taş duvarlar. Ahşap tavanlar. Safran kokulu bir şehir.";
  return (
    <section data-testid="pull-image-section" className="relative h-[60vh] min-h-[440px] overflow-hidden">
      <img src={REAL_PHOTOS.rooms_pull} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-[#0F0E0C]/40" />
      <div className="relative h-full flex items-center justify-center px-8">
        <p className="font-display italic text-[#FAF6EC] text-center max-w-4xl leading-[1.2] text-3xl sm:text-4xl lg:text-6xl">
          “{quote}”
        </p>
      </div>
    </section>
  );
};

/* ============ VIDEO GALLERY ============ */
const VideoGallery = ({ videos }) => {
  const { t, lang } = useLanguage();
  if (!videos.length) return null;
  return (
    <section data-testid="video-gallery-section" className="py-28 lg:py-40 bg-[#F2EDDF]">
      <div className="container-mag">
        <ChapterHeader num="04" eyebrow={t("gallery.videos_kicker")} title={t("gallery.videos_title")} />
        <div className="grid md:grid-cols-2 gap-6 lg:gap-10">
          {videos.map((v, idx) => (
            <Dialog key={v.id || idx}>
              <DialogTrigger asChild>
                <button data-testid={`video-thumb-${idx}`} className="relative group block w-full zoom-on-hover">
                  <img src={v.thumb} alt={v.title?.[lang] || v.title?.tr} className="w-full aspect-video object-cover" />
                  <div className="absolute inset-0 bg-[#0F0E0C]/35 group-hover:bg-[#0F0E0C]/25 transition-colors flex items-center justify-center">
                    <div className="w-20 h-20 border border-[#FAF6EC]/80 rounded-full flex items-center justify-center text-[#FAF6EC] group-hover:bg-[#FAF6EC] group-hover:text-[#0F0E0C] transition-all duration-500">
                      <Play size={22} fill="currentColor" />
                    </div>
                  </div>
                  <div className="absolute bottom-6 left-6 right-6 text-start font-display text-2xl text-[#FAF6EC]">
                    {v.title?.[lang] || v.title?.tr}
                  </div>
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-5xl p-0 bg-black border-0">
                <div className="aspect-video w-full">
                  <iframe title={v.title?.[lang] || v.title?.tr} src={`https://www.youtube.com/embed/${v.youtube_id}?autoplay=1`} allow="autoplay; encrypted-media" allowFullScreen className="w-full h-full" />
                </div>
              </DialogContent>
            </Dialog>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ============ TESTIMONIALS ============ */
const Testimonials = ({ items }) => {
  const { t, lang } = useLanguage();
  if (!items.length) return null;
  return (
    <section data-testid="testimonials-section" className="py-28 lg:py-40 bg-[#1E3B2E] text-[#FAF6EC]">
      <div className="container-narrow text-center">
        <div className="flex items-center justify-center gap-4 mb-12">
          <span className="font-mono text-[11px] uppercase tracking-[0.34em] text-[#A88A50]">No. 05</span>
          <span className="inline-block h-px w-12 bg-[#A88A50]" />
          <span className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#A88A50]">{t("testimonials.kicker")}</span>
        </div>
        <Carousel opts={{ loop: true, direction: lang === "ar" ? "rtl" : "ltr" }}>
          <CarouselContent>
            {items.map((t_, i) => (
              <CarouselItem key={t_.id || i}>
                <div data-testid={`testimonial-${i}`} className="px-4 lg:px-12">
                  <p className="font-display italic text-3xl sm:text-4xl lg:text-[3rem] leading-[1.25] text-[#FAF6EC] mb-10">
                    “{t_.text?.[lang] || t_.text?.tr}”
                  </p>
                  <div className="flex justify-center gap-1 text-[#A88A50] mb-3">
                    {Array.from({ length: t_.rating || 5 }).map((_, j) => (
                      <Star key={j} size={14} fill="currentColor" />
                    ))}
                  </div>
                  <div className="font-display text-lg text-[#FAF6EC]">{t_.name?.[lang] || t_.name?.tr}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.34em] text-[#FAF6EC]/55 mt-1">{t_.location?.[lang] || t_.location?.tr}</div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious data-testid="testimonials-prev" className="hidden md:flex bg-transparent text-[#FAF6EC] border-[#FAF6EC]/40" />
          <CarouselNext data-testid="testimonials-next" className="hidden md:flex bg-transparent text-[#FAF6EC] border-[#FAF6EC]/40" />
        </Carousel>
      </div>
    </section>
  );
};

/* ============ SAFRANBOLU ============ */
const SafranboluSection = ({ attractions }) => {
  const { t, lang } = useLanguage();
  return (
    <section id="safranbolu" data-testid="safranbolu-section" className="py-28 lg:py-40">
      <div className="container-mag">
        <ChapterHeader num="06" eyebrow={t("city.kicker")} title={t("city.title")} />

        <div className="grid lg:grid-cols-12 gap-10 mb-20">
          <div className="lg:col-span-7 lg:col-start-6">
            <p className="body-prose">{t("city.body")}</p>
          </div>
        </div>

        <h3 className="font-display text-2xl lg:text-3xl text-[#0F0E0C] mb-10 flex items-center gap-4">
          <span className="inline-block h-px w-10 bg-[#A88A50]" /> {t("city.attractions_title")}
        </h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16 mb-28">
          {attractions.map((a, i) => (
            <article key={a.id || i} data-testid={`attraction-${i}`} className="group">
              <div className="aspect-[4/5] overflow-hidden mb-6 zoom-on-hover">
                <img src={a.image} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#1E3B2E] mb-2">N° 0{i + 1}</div>
              <h4 className="display-md mb-3">{a.title?.[lang] || a.title?.tr}</h4>
              <p className="text-sm text-[#5C5448] leading-[1.85]">{a.description?.[lang] || a.description?.tr}</p>
            </article>
          ))}
        </div>

        <h3 className="font-display text-2xl lg:text-3xl text-[#0F0E0C] mb-10 flex items-center gap-4">
          <span className="inline-block h-px w-10 bg-[#A88A50]" /> {t("city.reach_title")}
        </h3>
        <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
          {[
            { icon: Bus, title: "city.from_istanbul", body: "city.from_istanbul_body" },
            { icon: Car, title: "city.from_ankara", body: "city.from_ankara_body" },
            { icon: Train, title: "city.by_train", body: "city.by_train_body" },
          ].map(({ icon: Icon, title, body }, i) => (
            <div key={title} data-testid={`travel-card-${i}`} className="border-t border-[#0F0E0C] pt-7">
              <div className="flex items-center justify-between mb-5">
                <Icon size={22} className="text-[#1E3B2E]" />
                <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#A88A50]">0{i + 1}</span>
              </div>
              <h4 className="display-md mb-3">{t(title)}</h4>
              <p className="body-prose text-sm">{t(body)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ============ CONTACT ============ */
const ContactMap = () => {
  const { t, lang } = useLanguage();
  const [contact, setContact] = useState({});
  useEffect(() => { api.get("/site/contact").then((r) => setContact(r.data)).catch(() => {}); }, []);
  const lat = parseFloat(contact.lat || "41.2543");
  const lng = parseFloat(contact.lng || "32.6950");
  const bbox = `${lng - 0.01}%2C${lat - 0.008}%2C${lng + 0.01}%2C${lat + 0.008}`;
  const osmUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat}%2C${lng}`;
  const address = contact[`address_${lang}`] || contact.address_tr || "";

  return (
    <section id="contact" data-testid="contact-section" className="py-28 lg:py-40 bg-[#F2EDDF]">
      <div className="container-mag">
        <ChapterHeader num="07" eyebrow={t("contact.kicker")} title={t("contact.title")} />

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5 space-y-8">
            {contact.phone && (
              <a href={`tel:${contact.phone.replace(/\s/g, "")}`} data-testid="contact-phone" className="block border-t border-[#0F0E0C] pt-6 group">
                <div className="font-mono text-[10px] uppercase tracking-[0.34em] text-[#5C5448] mb-2 flex items-center gap-3">
                  <Phone size={13} /> {t("contact.phone_label")}
                </div>
                <div className="font-display text-3xl lg:text-4xl text-[#0F0E0C] group-hover:text-[#1E3B2E] ltr-numbers transition-colors">{contact.phone}</div>
              </a>
            )}
            {contact.email && (
              <a href={`mailto:${contact.email}`} data-testid="contact-email" className="block border-t border-[#0F0E0C] pt-6 group">
                <div className="font-mono text-[10px] uppercase tracking-[0.34em] text-[#5C5448] mb-2 flex items-center gap-3">
                  <Mail size={13} /> {t("contact.email_label")}
                </div>
                <div className="text-xl text-[#0F0E0C] group-hover:text-[#1E3B2E] break-all transition-colors">{contact.email}</div>
              </a>
            )}
            {address && (
              <div data-testid="contact-address" className="border-t border-[#0F0E0C] pt-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.34em] text-[#5C5448] mb-2 flex items-center gap-3">
                  <MapPin size={13} /> {t("contact.address_label")}
                </div>
                <div className="text-lg text-[#0F0E0C] leading-relaxed">{address}</div>
              </div>
            )}
          </div>
          <div className="lg:col-span-7" data-testid="contact-map">
            <iframe title={t("contact.map_title")} src={osmUrl} className="w-full h-[520px] border-0" />
          </div>
        </div>
      </div>
    </section>
  );
};

const Home = () => {
  const [rooms, setRooms] = useState([]);
  const [content, setContent] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [videos, setVideos] = useState([]);
  const [attractions, setAttractions] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/rooms").then((r) => setRooms(r.data)),
      api.get("/site/content").then((r) => setContent(r.data)),
      api.get("/gallery").then((r) => setGallery(r.data)),
      api.get("/videos").then((r) => setVideos(r.data)),
      api.get("/attractions").then((r) => setAttractions(r.data)),
      api.get("/testimonials").then((r) => setTestimonials(r.data)),
    ]).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <SEO page="home" rooms={rooms} testimonials={testimonials} />
      <Header />
      <Hero content={content} />
      <About content={content} />
      <RoomsSection rooms={rooms} />
      <PullImage />
      <Gallery images={gallery} />
      <VideoGallery videos={videos} />
      <Testimonials items={testimonials} />
      <SafranboluSection attractions={attractions} />
      <ContactMap />
      <Footer />
      <MobileBookCTA />
      <WhatsAppButton />
    </div>
  );
};

export default Home;
